import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr, setToken, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, obj = auth
  const revalidating = useRef(false);
  const [space, setSpaceState] = useState(() => localStorage.getItem("ida_space") || "gestion");
  const setSpace = (s) => { localStorage.setItem("ida_space", s); setSpaceState(s); };

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // On a 401 from any protected call, re-verify the session ONCE.
    // Only log out if the session is really dead — a single stray 401
    // must not eject a still-authenticated user.
    setUnauthorizedHandler(async () => {
      if (revalidating.current) return;
      revalidating.current = true;
      try {
        const { data } = await api.get("/auth/me");
        setUser(data); // session still valid → keep the user logged in
      } catch {
        setToken(null);
        setUser(false);
      } finally {
        revalidating.current = false;
      }
    });
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      return; // AuthCallback handles it
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setToken(data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setSpace("gestion");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, checkAuth, space, setSpace }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiErr };
